// ============================================================
// v4 — the series: schedule + scoring + win conditions. Pure and
// dependency-free so it is unit-tested in Node and shared by the
// server (authoritative) and the client (rendering standings).
// A team (A/B) is stable across the session; the hider/seeker role
// rotates each round per `hiderTeamForRound`.
// ============================================================

export type Team = 'A' | 'B';

/** v29 BOLT-175 — where a round's replay lives.
 *
 *  `mp4Url` is ADDITIVE and OPTIONAL: iOS plays it inline (SwiftUI cannot
 *  animate a GIF at all, and 1.5 MB of GIF becomes ~60 KB of H.264), while the
 *  web `<img>` and the "Download replay" affordance keep using the GIF. It is
 *  optional rather than required because the encode is best-effort - a box with
 *  no ffmpeg still writes the GIF - and because every replay already on prod
 *  predates it. */
export interface ReplayUrls {
  gifUrl: string;
  jsonUrl: string;
  mp4Url?: string;
}
export type WinCondition = 'best' | 'cumulative' | 'mostRounds';

/** One completed round, appended to the series history. Scores only — never
 *  coordinates (the privacy invariant holds at the series layer too, §6). */
export interface RoundResult {
  roundIndex: number;
  hiderTeam: Team;
  timeHiddenMs: number;
  bonusMin: number;
  penaltyMin: number;
  finalMin: number;
  caught: boolean;
  replay: ReplayUrls | null;
}

/** Both-teams-visible standings (shown at intermission + game end). */
export interface Standings {
  winCondition: WinCondition;
  a: number; // team A's headline score under the condition
  b: number; // team B's headline score
  leader: Team | 'tie' | null; // null until at least one round is scored
}

const EPS = 1e-9;

export const otherTeam = (t: Team): Team => (t === 'A' ? 'B' : 'A');
export const totalRounds = (roundsPerTeam: number): number => roundsPerTeam * 2;

/** Which team hides round `i`, alternating from the first hider. */
export const hiderTeamForRound = (roundIndex: number, firstHider: Team): Team =>
  roundIndex % 2 === 0 ? firstHider : otherTeam(firstHider);

const sum = (xs: number[]) => xs.reduce((a, b) => a + b, 0);
const max0 = (xs: number[]) => (xs.length ? Math.max(...xs) : 0);
const scoresFor = (history: RoundResult[], team: Team): number[] =>
  history.filter((r) => r.hiderTeam === team).map((r) => r.finalMin);

/** Compare two score lists descending, lexicographically. >0 ⇒ A ahead. */
function cmpDescLists(a: number[], b: number[]): number {
  const as = [...a].sort((x, y) => y - x);
  const bs = [...b].sort((x, y) => y - x);
  const n = Math.max(as.length, bs.length);
  for (let i = 0; i < n; i++) {
    const d = (as[i] ?? 0) - (bs[i] ?? 0);
    if (Math.abs(d) > EPS) return d;
  }
  return 0;
}

const leaderFrom = (cmp: number): Team | 'tie' => (cmp > EPS ? 'A' : cmp < -EPS ? 'B' : 'tie');

/**
 * Evaluate standings over the rounds played so far (full spec §9):
 *  • best       — headline = best single round; tiebreak = next-best, then next…
 *  • cumulative — headline = sum; tiebreak = best single round
 *  • mostRounds — headline = pairs won (pair k = rounds 2k & 2k+1, higher hider
 *                 score wins the pair); tiebreak = best single round
 */
export function evaluateStandings(history: RoundResult[], winCondition: WinCondition): Standings {
  if (history.length === 0) return { winCondition, a: 0, b: 0, leader: null };
  const aScores = scoresFor(history, 'A');
  const bScores = scoresFor(history, 'B');

  if (winCondition === 'cumulative') {
    const a = sum(aScores);
    const b = sum(bScores);
    let cmp = a - b;
    if (Math.abs(cmp) <= EPS) cmp = max0(aScores) - max0(bScores); // tiebreak: best single
    return { winCondition, a, b, leader: leaderFrom(cmp) };
  }

  if (winCondition === 'mostRounds') {
    const byIndex = new Map(history.map((r) => [r.roundIndex, r] as const));
    const maxIdx = Math.max(...history.map((r) => r.roundIndex));
    let aWins = 0;
    let bWins = 0;
    for (let k = 0; 2 * k + 1 <= maxIdx; k++) {
      const r0 = byIndex.get(2 * k);
      const r1 = byIndex.get(2 * k + 1);
      if (!r0 || !r1) continue; // incomplete pair — not yet decided
      const aRound = r0.hiderTeam === 'A' ? r0 : r1;
      const bRound = r0.hiderTeam === 'B' ? r0 : r1;
      if (aRound.finalMin > bRound.finalMin + EPS) aWins++;
      else if (bRound.finalMin > aRound.finalMin + EPS) bWins++;
      // exact pair tie → no winner for that pair
    }
    let cmp = aWins - bWins;
    if (cmp === 0) cmp = cmpDescLists(aScores, bScores); // tiebreak: best single, then next
    return { winCondition, a: aWins, b: bWins, leader: leaderFrom(cmp) };
  }

  // best (default)
  return {
    winCondition,
    a: max0(aScores),
    b: max0(bScores),
    leader: leaderFrom(cmpDescLists(aScores, bScores)),
  };
}
